import { Link } from "react-router-dom";
import { FaEnvelope, FaShareAlt, FaPlus } from "react-icons/fa";
import duliaLogoNavy from "../components/Dulia2Navy.png";
import duliaLogoGold from "../components/Dulia2Gold.png";
import { useEffect, useState } from "react";
import { useTheme } from "../hooks/useTheme";

export default function About() {
  const { isDark } = useTheme();
  const [resolvedIsDark, setResolvedIsDark] = useState(() => isDark);
  const [shareStatus, setShareStatus] = useState("");

  useEffect(() => {
    document.title = "About Dulia";
  }, []);

  useEffect(() => {
    setResolvedIsDark(isDark);
  }, [isDark]);

  useEffect(() => {
    const root = document.documentElement;

    function syncTheme() {
      setResolvedIsDark(root.classList.contains("dark"));
    }

    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const btn =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-dulia-line bg-dulia-panel px-4 py-2 text-sm font-medium text-dulia-navy shadow-soft transition " +
    "hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night";

  const linkGold =
    "font-medium text-dulia-navy underline decoration-dulia-gold/60 underline-offset-4 transition hover:decoration-dulia-gold " +
    "dark:text-dulia-goldSoft dark:hover:text-dulia-parchment";

  async function onShare() {
    const url = "https://dulia.us";

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Dulia",
          text: "Dulia — a living index of the saints.",
          url,
        });
        return;
      } catch {
        // fall back to copy
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setShareStatus("Link copied");
      window.setTimeout(() => setShareStatus(""), 1500);
    } catch {
      setShareStatus("Copy failed");
      window.setTimeout(() => setShareStatus(""), 1500);
    }
  }

  return (
    <div className="relative space-y-6">
      {/* Logo: absolute on larger screens, inline on small screens so it never covers text */}
      <img
        src={resolvedIsDark ? duliaLogoGold : duliaLogoNavy}
        alt="Dulia"
        loading="lazy"
        className="
          pointer-events-none
          mx-auto h-24 w-24 object-contain
          sm:mx-0 sm:absolute sm:right-0 sm:top-0 sm:h-40 sm:w-40
        "
      />

      {/* Back button stays in normal flow */}
      <div>
        <Link to="/" className={btn}>
          ← Home
        </Link>
      </div>

      {/* Letter content - add right padding only when the logo is absolute */}
      <div className="sm:pr-44">
        <h1 className="text-3xl font-bold">About Dulia</h1>
        <div className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
          <p>Hello,</p>
          <p>
            My name is Tyler. I am the creator of Dulia. This page exists to
            answer a few questions you might have.
          </p>
        </div>

        <h2 className="mt-6 text-xl font-semibold">What is Dulia?</h2>
        <div className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
          <p>
            Dulia is a saint aggregator. <em>Dulia</em> is the Latin term for the
            veneration and respect Catholics pay to the saints and the angels.
            There is no shortage of information about the thousands of saints
            out there on the Internet, but there hasn&apos;t been a single place
            where you could find complete pictures of their lives. You&apos;d have
            to look in one place for their life story, another for their art,
            and one or two more places for quotes or prayers. And that&apos;s just
            for the saints you already know - how about discovering new ones?
          </p>
          <p>
            The goal of Dulia was to take all of the information that&apos;s floating
            around out there and make it more easily accessible. This app was
            built with the database structure, UI layout, and features mostly
            already figured out. Dulia is set up in such a way that makes
            requesting, creating, and editing profiles both a community effort
            and an easy process.
          </p>
          <p>
            All you have to do is sign in with a Google account, and you can
            request a profile. Just fill out the form with the necessary
            information. One of my admins (or myself) will take a look at it,
            tweak whatever we need to, and get it added! You can help us bring
            the saints to the world, and I think that&apos;s pretty sweet.
          </p>
        </div>

        <h2 className="mt-6 text-xl font-semibold">How can I help?</h2>
        <div className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
          <p>
            Currently, Dulia&apos;s biggest need from a tech perspective is simply
            more profiles. You don&apos;t have to ask me to help with this part - just
            sign in and do what I described above. They don&apos;t take very long to
            make, and even just a few in your spare time would be greatly
            appreciated.
          </p>
          <p>
            Dulia is a community effort. There are undoubtedly countless great
            ideas people will have that I haven&apos;t thought of. Maybe you spot an
            issue somewhere. Whatever the case, even if you just want to reach
            out, send me an email! I&apos;d love to hear from you.
          </p>
          <p>
            Finally, apart from requesting more profiles, I&apos;d greatly appreciate
            if you shared Dulia with someone. I can see students, teachers,
            clergy, and just a regular Joe Shmo like myself all benefitting from
            Dulia as it continues to grow.
          </p>
        </div>

        <h2 className="mt-6 text-xl font-semibold">Who are you?</h2>
        <div className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
          <p>
            My name is Tyler. I am 23 years old and work as a software
            engineer during the day. Outside of work, I love to play hockey
            (including 2 years for the UCF club team - go Knights!), board
            games, and the guitar. I also enjoy personal projects like these, so
            making one to serve God and His Church in such a unique way is a
            great honor.
          </p>
          <p>
            Throughout 2025, I volunteered with the JMJ Pregnancy Center (a Catholic pro-life
            pregnancy center in Orlando) to create a brand-new appointment booking system from scratch. Now, in 2026, I felt a call to do something more
            directly serving the Church. My line of work doesn&apos;t really avail
            itself to much opportunities when it comes to volunteering, so I
            created one myself.
          </p>
          <p>
            I also want to give a shoutout to my friends Enzo Torlone, Clare Abshire, and Jade and Anthony Saba. They all have been a huge help in getting Dulia off the ground.
          </p>
        </div>

        <h2 className="mt-6 text-xl font-semibold">What&apos;s next?</h2>
        <div className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
          <p>
            We&apos;ve got a few big, shiny new features coming down the pipeline for
            Dulia that will greatly enhance what it has to offer. I think
            there are other things, beyond just the saints, that we can add to
            this app that would help people learn and grow a ton.
          </p>
          <p>
            For now, the three biggest ways you can help Dulia&apos;s mission are
            requesting more profiles, sharing the app, and reaching out with
            ideas. I&apos;ve conveniently placed three buttons beneath this text to do
            just that!
          </p>
          <p>
            Additionally, I ask for your prayers for Dulia and for myself. They
            will be far more impactful than any of the practicals I already
            mentioned. We pray for the intercession of the entire Church Triumphant in Heaven
            as this app continues to grow to proclaim their witness
            and the glory of God.
          </p>
          <p>
            Particularly, we pray for the intercession of{" "}
            <Link to="/profile/saint-carlo-acutis" className={linkGold}>
              St. Carlo Acutis
            </Link>
            , whose example of using technology for the Church directly inspired
            this endeavor.
          </p>
        </div>

        {/* Action buttons */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Link to="/request/" className={btn}>
            <FaPlus aria-hidden="true" />
            Request a Profile
          </Link>

          <button type="button" onClick={onShare} className={btn}>
            <FaShareAlt aria-hidden="true" />
            Share Dulia
          </button>

          <a href="mailto:tyler.beach@ucf.edu" className={btn} title="Email Tyler">
            <FaEnvelope aria-hidden="true" />
            Email Me
          </a>

          {shareStatus ? (
            <span className="text-sm text-dulia-navy/70 dark:text-dulia-parchment/70">
              {shareStatus}
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
        <p>Thank you and God bless,</p>
        <p>Tyler</p>
        <p>
          <em>AMDG</em>
        </p>
      </div>
    </div>
  );
}